import React from 'react'
import { EyeOff, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { format } from 'date-fns'
import InstagramPreview from '@/features/social/components/preview/InstagramPreview'
import SocialPreview from '@/features/social/components/preview/SocialPreview'
import type { SocialPost, SocialProfile } from '@/features/social/types'

interface PostPreviewSectionProps {
  enhancedUI: boolean
  platform?: string
  postData: Partial<SocialPost>
  currentProfile?: SocialProfile | null
  queueName?: string
  scheduleSummary?: string
  status: string
  validationActive: boolean
  validationMessage?: string
}

export function PostPreviewSection({
  enhancedUI,
  platform,
  postData,
  currentProfile,
  queueName,
  scheduleSummary,
  status,
  validationActive,
  validationMessage
}: PostPreviewSectionProps) {
  if (enhancedUI) {
    return (
      <div className="space-y-6">
        <SocialPreview
          platform={platform || 'INSTAGRAM'}
          postData={postData}
          accountName={currentProfile?.name || 'fixmybrick'}
          accountAvatar={currentProfile?.profile_picture_url || undefined}
        />

        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-semibold">Publishing Summary</CardTitle>
            <CardDescription>Destination, timing, and status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Destination</p>
                <p className="text-sm font-semibold text-foreground mt-1">
                  {currentProfile?.name || 'Select a profile'}
                </p>
              </div>
              {queueName && (
                <Badge variant="outline" className="text-xs uppercase tracking-wide">
                  {queueName}
                </Badge>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Timing</p>
                <p className="text-sm font-semibold text-foreground mt-1">
                  {postData.scheduled_time ? format(new Date(postData.scheduled_time), 'PP • p') : 'Publishes immediately'}
                </p>
              </div>
              <Badge variant="secondary">
                {postData.scheduled_time ? 'Scheduled' : 'Instant'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                <p className="text-sm font-semibold text-foreground mt-1">
                  {status}
                </p>
              </div>
              {validationActive && (
                <Badge variant="destructive" className="gap-1 text-xs">
                  <AlertCircle className="h-3 w-3" />
                  {validationMessage}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="sticky top-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Live Preview</h3>
        <Badge variant="outline" className="bg-background">
          {platform || 'Select Profile'}
        </Badge>
      </div>

      {platform === 'INSTAGRAM' ? (
        <div className="bg-background rounded-xl border shadow-sm overflow-hidden">
          <InstagramPreview
            caption={postData.caption || ''}
            images={postData.image_urls || []}
            hashtags={postData.hashtags || []}
            accountName={currentProfile?.name || 'fixmybrick'}
            accountAvatar={currentProfile?.profile_picture_url || undefined}
          />
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground space-y-4">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
              <EyeOff className="h-6 w-6 opacity-50" />
            </div>
            <div>
              <p className="font-medium">Preview not available</p>
              <p className="text-sm mt-1">Select Instagram profile to see preview</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mt-6 p-4 rounded-lg bg-blue-50 border border-blue-100 text-blue-800 text-sm">
        <div className="flex items-start gap-2">
          <div className="mt-0.5">💡</div>
          <p>
            <strong>Pro Tip:</strong> Use high-quality images and relevant hashtags to increase engagement.
            Instagram allows up to 30 hashtags per post.
          </p>
        </div>
      </div>
    </div>
  )
}

